import React, { useState, useEffect } from 'react';
import { usersApi, messagesApi, operatorPermissionsApi } from '../services/api';
import api from '../services/api';
import { User } from '../contexts/AuthContext';
import ClientesSelectorComponent from './ClientesSelectorComponent';
import UserDetailDashboard from './UserDetailDashboard';
import ImpersonationDashboard from './ImpersonationDashboard';

interface UserRecord {
  id: number;
  email: string;
  name: string;
  role: string;
  isActive: boolean;
  permissions: {
    esAdministradorGrupo?: boolean;
    clientesAdministrados?: number[];
    nombreGrupo?: string | null;
    [key: string]: any;
  };
  clienteId?: number;
  lastLogin?: string;
  createdAt: string;
  updatedAt: string;
}

interface ClientData {
  id: number;
  name: string;
  empresa: string;
  email: string;
}

interface AssignedClient {
  id: number;
  operatorId: number;
  clientId: number;
  assignedBy: number;
  createdAt: string;
  client: ClientData;
  assignedByUser: {
    id: number;
    name: string;
    email: string;
  };
}

interface UserManagementProps {
  user: User;
}


const UserManagement: React.FC<UserManagementProps> = ({ user }) => {
  const [users, setUsers] = useState<UserRecord[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<UserRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingUser, setEditingUser] = useState<UserRecord | null>(null);
  const [showUserDetail, setShowUserDetail] = useState<UserRecord | null>(null);
  const [showUserDashboard, setShowUserDashboard] = useState<UserRecord | null>(null);
  const [formData, setFormData] = useState({
    email: '',
    username: '',
    password: '',
    name: '',
    role: 'cliente',
    companyName: '', // Para clientes
    companyAddress: '', // Para clientes
    companyPhone: '', // Para clientes
    permissions: {},
    // Campos para ejecutivos
    comision_captacion: '',
    comision_cobro: '',
    tipo_comision_captacion: 'porcentaje',
    tipo_comision_cobro: 'porcentaje'
  });
  const [showPassword, setShowPassword] = useState(false);
  const [changePassword, setChangePassword] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('todos');
  const [messageContent, setMessageContent] = useState('');
  const [userMessages, setUserMessages] = useState<any[]>([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sueldoData, setSueldoData] = useState<any>(null);
  const [workSessionData, setWorkSessionData] = useState<any>(null);
  const [loadingUserDetails, setLoadingUserDetails] = useState(false);
  const [usersSueldos, setUsersSueldos] = useState<Record<number, any>>({});

  // Estados para historial de logins
  const [loginHistory, setLoginHistory] = useState<any[]>([]);
  const [loadingLoginHistory, setLoadingLoginHistory] = useState(false);
  const [showLoginHistory, setShowLoginHistory] = useState(false);

  // Estados para gestión de clientes asignados
  const [showClientsModal, setShowClientsModal] = useState(false);
  const [selectedOperator, setSelectedOperator] = useState<UserRecord | null>(null);
  const [availableClients, setAvailableClients] = useState<ClientData[]>([]);
  const [assignedClients, setAssignedClients] = useState<AssignedClient[]>([]);
  const [clientsLoading, setClientsLoading] = useState(false);

  const roles = [
    { value: 'admin', label: 'Administrador' },
    { value: 'supervisor', label: 'Supervisor' },
    { value: 'operador', label: 'Operador' },
    { value: 'cliente', label: 'Cliente' },
    { value: 'contable', label: 'Contable' },
    { value: 'ejecutivo', label: 'Ejecutivo' }
  ];

  useEffect(() => {
    loadUsers();
  }, []);

  useEffect(() => {
    // Cargar sueldos de todos los empleados cuando cambien los usuarios filtrados
    loadAllUsersSueldos();
  }, [filteredUsers]);

  useEffect(() => {
    filterUsers();
  }, [users, searchTerm, roleFilter]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const response = await usersApi.getAll();
      if (response.data.success) {
        setUsers(response.data.data);
      }
    } catch (error: any) {
      console.error('Error cargando usuarios:', error);
      setError('Error cargando usuarios');
    } finally {
      setLoading(false);
    }
  };

  const loadLoginHistory = async (userId: number) => {
    try {
      setLoadingLoginHistory(true);
      const response = await usersApi.getLoginHistory(userId, 50);
      if (response.data.success) {
        setLoginHistory(response.data.data);
      }
    } catch (error: any) {
      console.error('Error cargando historial de logins:', error);
      setLoginHistory([]);
    } finally {
      setLoadingLoginHistory(false);
    }
  };

  const filterUsers = () => {
    let filtered = users;

    // Filtrar por término de búsqueda
    if (searchTerm.trim()) {
      filtered = filtered.filter(userRecord =>
        userRecord.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        userRecord.email.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filtrar por rol
    if (roleFilter !== 'todos') {
      filtered = filtered.filter(userRecord => userRecord.role === roleFilter);
    }

    setFilteredUsers(filtered);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Validaciones básicas y simples
    if (!formData.name.trim()) {
      setError('El nombre es obligatorio');
      return;
    }
    if (!formData.email.trim()) {
      setError('El email es obligatorio');
      return;
    }

    try {
      if (editingUser) {
        // ACTUALIZAR USUARIO EXISTENTE
        const updateData: any = {
          name: formData.name.trim(),
          email: formData.email.trim(),
          role: formData.role,
          permissions: formData.permissions || {}
        };

        // Agregar campos de comisión si es ejecutivo
        if (formData.role === 'ejecutivo') {
          updateData.comision_captacion = parseFloat(formData.comision_captacion) || 0;
          updateData.comision_cobro = parseFloat(formData.comision_cobro) || 0;
          updateData.tipo_comision_captacion = formData.tipo_comision_captacion;
          updateData.tipo_comision_cobro = formData.tipo_comision_cobro;
        }

        // Solo agregar username si existe
        if (formData.username?.trim()) {
          updateData.username = formData.username.trim();
        }

        // Agregar contraseña si hay algo escrito (independiente del checkbox)
        if (formData.password?.trim()) {
          if (formData.password.trim().length < 6) {
            setError('La contraseña debe tener al menos 6 caracteres');
            return;
          }
          updateData.password = formData.password.trim();
        }

        // Actualizar usuario
        const response = await usersApi.update(editingUser.id, updateData);
        
        if (!response.data.success) {
          setError(response.data.message || 'Error al actualizar usuario');
          return;
        }

        // Si es cliente, actualizar datos de empresa (opcional, sin romper si falla)
        if (formData.role === 'cliente' && editingUser.clienteId) {
          try {
            if (formData.companyName?.trim() || formData.companyAddress?.trim() || formData.companyPhone?.trim()) {
              await api.put(`/clients/${editingUser.clienteId}`, {
                nombre: formData.companyName?.trim() || '',
                direccion: formData.companyAddress?.trim() || '',
                telefono: formData.companyPhone?.trim() || ''
              });
            }
          } catch (clientError) {
            // Si falla actualizar cliente, no importa, el usuario se actualizó bien
          }
        }

        setSuccess('✅ Usuario actualizado correctamente');
        loadUsers();
        resetForm();
      } else {
        // CREAR NUEVO USUARIO
        if (!formData.password?.trim()) {
          setError('La contraseña es obligatoria para usuarios nuevos');
          return;
        }
        if (formData.password.trim().length < 6) {
          setError('La contraseña debe tener al menos 6 caracteres');
          return;
        }

        const createData: any = {
          email: formData.email.trim(),
          password: formData.password.trim(),
          name: formData.name.trim(),
          role: formData.role,
          permissions: formData.permissions || {}
        };

        if (formData.username?.trim()) {
          createData.username = formData.username.trim();
        }

        // Agregar campos de comisión si es ejecutivo
        if (formData.role === 'ejecutivo') {
          createData.comision_captacion = parseFloat(formData.comision_captacion) || 0;
          createData.comision_cobro = parseFloat(formData.comision_cobro) || 0;
          createData.tipo_comision_captacion = formData.tipo_comision_captacion;
          createData.tipo_comision_cobro = formData.tipo_comision_cobro;
        }

        const response = await usersApi.create(createData);
        
        if (!response.data.success) {
          setError(response.data.message || 'Error al crear usuario');
          return;
        }

        setSuccess(`✅ Usuario creado exitosamente

🔑 CREDENCIALES:
📧 Email: ${formData.email}
🔒 Password: ${formData.password}
👤 Rol: ${formData.role}`);

        loadUsers();
        resetForm();
      }
    } catch (error: any) {
      console.error('Error:', error);
      const errorMessage = error.response?.data?.error || 
                          error.response?.data?.message || 
                          error.message || 
                          'Error desconocido';
      setError(errorMessage);
    }
  };

  const resetForm = () => {
    setFormData({
      email: '',
      username: '',
      password: '',
      name: '',
      role: 'cliente',
      companyName: '',
      companyAddress: '',
      companyPhone: '',
      permissions: {},
      comision_captacion: '',
      comision_cobro: '',
      tipo_comision_captacion: 'porcentaje',
      tipo_comision_cobro: 'porcentaje'
    });
    setEditingUser(null);
    setShowForm(false);
    setChangePassword(false);
    setShowPassword(false);
    setError('');
    setSuccess('');
  };

  const generatePassword = () => {
    const length = 12;
    const charset = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%&*";
    let password = "";
    for (let i = 0; i < length; i++) {
      password += charset.charAt(Math.floor(Math.random() * charset.length));
    }
    setFormData({...formData, password});
    setChangePassword(true);
  };

  const handleDelete = async (userRecord: UserRecord) => {
    
    if (!window.confirm(`¿Estás seguro de que quieres eliminar al usuario "${userRecord.name}"?\n\nEsta acción no se puede deshacer.`)) {
      return;
    }

    try {
      setError('');
      setSuccess('');
      
      const response = await usersApi.delete(userRecord.id);
      
      if (response.data.success) {
        setSuccess(`Usuario "${userRecord.name}" eliminado exitosamente`);
        loadUsers();
      } else {
        setError('Error: El backend no pudo eliminar el usuario');
      }
    } catch (error: any) {
      console.error('❌ Error eliminando usuario:', error);
      console.error('❌ Respuesta completa:', error.response);
      setError(error.response?.data?.error || 'Error eliminando usuario');
    }
  };

  const clearMessages = () => {
    setError('');
    setSuccess('');
  };

  const handleEdit = async (userRecord: UserRecord) => {
    clearMessages(); // Limpiar mensajes al abrir edición
    try {
      // Obtener los detalles completos del usuario incluyendo datos de cliente si aplica
      const response = await usersApi.getById(userRecord.id);
      const fullUserData = response.data.data;
      
      
      // Si es un cliente, obtener datos de la empresa
      let companyData = {
        companyName: '',
        companyAddress: '',
        companyPhone: ''
      };

      if (fullUserData.role === 'cliente' && fullUserData.clienteId) {
        try {
          const clientResponse = await api.get(`/clients/${fullUserData.clienteId}`);
          if (clientResponse.data.success) {
            const clientData = clientResponse.data.data;
            companyData = {
              companyName: clientData.nombre || '',
              companyAddress: clientData.direccion || '',
              companyPhone: clientData.telefono || ''
            };
          }
        } catch (clientError) {
        }
      }
      
      setFormData({
        email: fullUserData.email,
        username: fullUserData.username || '',
        password: '', // NO mostrar contraseña actual, dejar vacío
        name: fullUserData.name,
        role: fullUserData.role,
        ...companyData, // Cargar datos reales de la empresa
        permissions: fullUserData.permissions || {},
        comision_captacion: fullUserData.comision_captacion?.toString() || '',
        comision_cobro: fullUserData.comision_cobro?.toString() || '',
        tipo_comision_captacion: fullUserData.tipo_comision_captacion || 'porcentaje',
        tipo_comision_cobro: fullUserData.tipo_comision_cobro || 'porcentaje'
      });
      setChangePassword(false); // Asegurar que esté desmarcado por defecto
      setEditingUser(userRecord);
      setShowForm(true);
    } catch (error) {
      console.error('Error obteniendo detalles del usuario:', error);
      // Fallback en caso de error
      setFormData({
        email: userRecord.email,
        username: userRecord.username || '',
        password: '',
        name: userRecord.name,
        role: userRecord.role,
        companyName: '',
        companyAddress: '',
        companyPhone: '',
        permissions: userRecord.permissions || {},
        comision_captacion: '',
        comision_cobro: '',
        tipo_comision_captacion: 'porcentaje',
        tipo_comision_cobro: 'porcentaje'
      });
      setEditingUser(userRecord);
      setShowForm(true);
    }
  };

  const handleToggleActive = async (userId: number, isActive: boolean) => {
    try {
      const response = await usersApi.activate(userId, !isActive);
      if (response.data.success) {
        setSuccess(`Usuario ${!isActive ? 'activado' : 'desactivado'} exitosamente`);
        loadUsers();
      }
    } catch (error: any) {
      console.error('Error cambiando estado:', error);
      setError(error.response?.data?.error || 'Error cambiando estado del usuario');
    }
  };

  const getRoleColor = (role: string) => {
    const colors = {
      admin: { bg: '#fef3c7', text: '#92400e' },
      supervisor: { bg: '#ddd6fe', text: '#5b21b6' },
      operador: { bg: '#bfdbfe', text: '#1e40af' },
      cliente: { bg: '#d1fae5', text: '#065f46' },
      contable: { bg: '#fce7f3', text: '#be185d' },
      ejecutivo: { bg: '#fed7aa', text: '#c2410c' }
    };
    return colors[role as keyof typeof colors] || { bg: '#f3f4f6', text: '#374151' };
  };

  const formatMoney = (amount: number) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS'
    }).format(amount || 0);
  };

  const getProgressColor = (porcentaje: number) => {
    const valor = parseFloat(porcentaje.toString()) || 0;
    if (valor < 30) return '#ef4444'; // Rojo
    if (valor < 70) return '#f59e0b'; // Amarillo
    if (valor < 100) return '#10b981'; // Verde
    return '#8b5cf6'; // Púrpura para más del 100%
  };

  const getTimeAlert = (horasActuales: number) => {
    if (horasActuales > 12) return { level: 'critical', message: '🚨 Exceso crítico de horas!' };
    if (horasActuales > 10) return { level: 'warning', message: '⚠️ Horas excesivas' };
    if (horasActuales > 8) return { level: 'caution', message: '⏰ Horas extra' };
    return null;
  };

  const loadUserMessages = async (userId: number) => {
    try {
      setLoadingMessages(true);
      const response = await messagesApi.getUserMessages(userId);
      if (response.data.success) {
        setUserMessages(response.data.data);
      }
    } catch (error) {
      console.error('Error cargando mensajes:', error);
    } finally {
      setLoadingMessages(false);
    }
  };

  const loadAllUsersSueldos = async () => {
    // Solo cargar para admin y contable
    if (!['admin', 'contable'].includes(user.role)) {
      return;
    }

    const empleados = filteredUsers.filter(u =>
      ['operador', 'supervisor', 'admin', 'contable'].includes(u.role)
    );

    const sueldosData: Record<number, any> = {};

    try {
      const currentMonth = new Date().getMonth() + 1;
      const currentYear = new Date().getFullYear();

      for (const empleado of empleados) {
        try {
          const sueldoResponse = await api.get(`/sueldos?operadorId=${empleado.id}&mes=${currentMonth}&anio=${currentYear}`);

          if (sueldoResponse.data.success && sueldoResponse.data.data && sueldoResponse.data.data.length > 0) {
            const sueldoInfo = sueldoResponse.data.data[0];

            sueldosData[empleado.id] = {
              id: sueldoInfo.id,
              sueldoFinal: sueldoInfo.sueldoFinal || 0,
              porcentajeObjetivo: sueldoInfo.porcentajeObjetivo || 0,
              estado: sueldoInfo.estado || 'calculando',
              tipoPago: sueldoInfo.tipoPago || 'objetivos',
              auditoriasHechas: sueldoInfo.auditoriasHechas || 0,
              correccionesHechas: sueldoInfo.correccionesHechas || 0,
              pagoFrancos: sueldoInfo.pagoFrancos || 0
            };
          }
        } catch (error) {
          console.error(`Error cargando sueldo de ${empleado.name}:`, error);
        }
      }

      setUsersSueldos(sueldosData);
    } catch (error) {
      console.error('Error cargando sueldos de empleados:', error);
    }
  };

  const loadUserSueldoAndWorkData = async (usuario: UserRecord) => {
    // Solo cargar datos de sueldo para empleados (no clientes)
    if (!['operador', 'supervisor', 'admin', 'contable'].includes(usuario.role)) {
      setSueldoData(null);
      setWorkSessionData(null);
      return;
    }

    // RESTRICCIÓN: Solo admin y contable pueden ver sueldos de otros usuarios
    if (user.role === 'supervisor' && usuario.id !== user.id) {
      setSueldoData(null);
      setWorkSessionData(null);
      return;
    }

    setLoadingUserDetails(true);
    setSueldoData(null);
    setWorkSessionData(null);
    
    try {
      // Cargar datos del sueldo
      const sueldoResponse = await api.get(`/sueldos?operadorId=${usuario.id}&mes=${new Date().getMonth() + 1}&anio=${new Date().getFullYear()}`);
      
      if (sueldoResponse.data.success && sueldoResponse.data.data && sueldoResponse.data.data.length > 0) {
        const sueldoInfo = sueldoResponse.data.data[0];
        
        // Obtener pagos realizados a este empleado
        let totalPagado = 0;
        try {
          const pagosResponse = await api.get(`/finanzas/pagos-empleado/${usuario.id}?mes=${new Date().getMonth() + 1}&anio=${new Date().getFullYear()}`);
          if (pagosResponse.data.success) {
            totalPagado = pagosResponse.data.data.totalPagado || 0;
          }
        } catch (pagosError) {
        }
        
        const sueldoOriginal = parseFloat(sueldoInfo.sueldoFinal) || parseFloat(sueldoInfo.sueldoReferencia) || 0;
        const sueldoPendiente = Math.max(0, sueldoOriginal - totalPagado);

        const sueldoFormatted = {
          sueldo: {
            ...sueldoInfo,
            porcentajeObjetivo: parseFloat(sueldoInfo.porcentajeObjetivo) || 0,
            auditoriasHechas: parseInt(sueldoInfo.auditoriasHechas) || 0,
            correccionesHechas: parseInt(sueldoInfo.correccionesHechas) || 0
          },
          mensaje: `Sueldo ${usuario.role}: $${parseFloat(sueldoInfo.sueldoReferencia || 0).toLocaleString()}`,
          detalle: {
            sueldoReferencia: parseFloat(sueldoInfo.sueldoReferencia) || 0,
            diasPagos: {
              total: (parseInt(sueldoInfo.domingosMes) || 0) + (parseInt(sueldoInfo.feriadosMes) || 0),
              monto: parseFloat(sueldoInfo.valorDiaPago || 0) * ((parseInt(sueldoInfo.domingosMes) || 0) + (parseInt(sueldoInfo.feriadosMes) || 0))
            },
            objetivo: {
              monto: parseFloat(sueldoInfo.sueldoReferencia || 0) - (parseFloat(sueldoInfo.valorDiaPago || 0) * ((parseInt(sueldoInfo.domingosMes) || 0) + (parseInt(sueldoInfo.feriadosMes) || 0))),
              porcentaje: parseFloat(sueldoInfo.porcentajeObjetivo) || 0,
              montoGanado: parseFloat(sueldoInfo.montoAuditorias) || 0
            },
            auditorias: {
              hechas: parseInt(sueldoInfo.auditoriasHechas) || 0,
              monto: parseFloat(sueldoInfo.montoAuditorias) || 0
            },
            plus: {
              tipo: sueldoInfo.plusTipo || 'porcentaje',
              porcentaje: parseFloat(sueldoInfo.plusPorcentaje) || 0,
              montoFijo: parseFloat(sueldoInfo.plusMontoFijo) || 0,
              monto: 0
            },
            total: sueldoPendiente,
            sueldoOriginal: sueldoOriginal,
            totalPagado: totalPagado
          }
        };
        
        
        setSueldoData(sueldoFormatted);
      }

      // Cargar datos de control de tiempo
      try {
        const workResponse = await api.get(`/work-sessions/user-hours/${usuario.id}`);
        if (workResponse.data.success) {
          const workData = workResponse.data.data;
          
          setWorkSessionData({
            sesionActiva: workData.sesionActiva || false,
            horasActuales: workData.horasTrabajadas || 0,
            horasRegulares: workData.horasRegulares || 0,
            horasExtra: workData.horasExtra || 0,
            necesitaAprobacion: (workData.horasTrabajadas || 0) > 8,
            alertaEnviada: (workData.horasTrabajadas || 0) > 10,
            message: `${workData.auditoriasCompletadas || 0} auditorías completadas hoy`
          });
        } else {
          // Fallback: generar datos realistas basados en auditorías
          const horasReales = sueldoData ? Math.max(sueldoData.detalle.auditorias.hechas * 1.5, 6) : 8;
          setWorkSessionData({
            sesionActiva: horasReales > 0,
            horasActuales: horasReales,
            horasRegulares: Math.min(horasReales, 8),
            horasExtra: Math.max(0, horasReales - 8),
            necesitaAprobacion: horasReales > 8,
            alertaEnviada: horasReales > 10,
            message: `Estimado basado en auditorías del mes`
          });
        }
      } catch (workError) {
        // Datos por defecto
        const horasDefault = usuario.role === 'admin' ? 7 : usuario.role === 'supervisor' ? 8.5 : 8;
        setWorkSessionData({
          sesionActiva: false,
          horasActuales: horasDefault,
          horasRegulares: Math.min(horasDefault, 8),
          horasExtra: Math.max(0, horasDefault - 8),
          necesitaAprobacion: horasDefault > 8,
          alertaEnviada: false,
          message: 'Datos de ejemplo'
        });
      }

    } catch (error) {
      console.error('Error cargando detalles del usuario:', error);
      setSueldoData(null);
      setWorkSessionData(null);
    } finally {
      setLoadingUserDetails(false);
    }
  };

  const handleSendMessage = async (tipo: 'mensaje' | 'alerta') => {
    if (!showUserDetail || !messageContent.trim()) return;

    try {
      const response = await messagesApi.send({
        toUserId: showUserDetail.id,
        contenido: messageContent,
        tipo,
        prioridad: tipo === 'alerta' ? 'alta' : 'normal'
      });

      if (response.data.success) {
        setSuccess(`${tipo === 'mensaje' ? 'Mensaje' : 'Alerta'} enviado exitosamente`);
        setMessageContent('');
        // Recargar mensajes
        loadUserMessages(showUserDetail.id);
      }
    } catch (error: any) {
      console.error('Error enviando mensaje:', error);
      setError(error.response?.data?.error || 'Error enviando mensaje');
    }
  };

  // === FUNCIONES PARA GESTIÓN DE CLIENTES ASIGNADOS ===

  const handleManageClients = async (operator: UserRecord) => {
    setSelectedOperator(operator);
    setShowClientsModal(true);
    setClientsLoading(true);

    try {
      // Cargar clientes disponibles y asignados en paralelo
      const [availableResponse, assignedResponse] = await Promise.all([
        operatorPermissionsApi.getAvailableClients(operator.id),
        operatorPermissionsApi.getAssignedClients(operator.id)
      ]);

      if (availableResponse.data.success) {
        setAvailableClients(availableResponse.data.data);
      }

      if (assignedResponse.data.success) {
        setAssignedClients(assignedResponse.data.data);
      }
    } catch (error: any) {
      console.error('Error cargando clientes:', error);
      setError('Error cargando clientes: ' + (error.response?.data?.error || error.message));
    } finally {
      setClientsLoading(false);
    }
  };

  const handleAssignClient = async (clientId: number) => {
    if (!selectedOperator) return;

    try {
      const response = await operatorPermissionsApi.assignClient(
        selectedOperator.id,
        clientId
      );

      if (response.data.success) {
        setSuccess('Cliente asignado exitosamente');
        // Recargar listas
        await handleManageClients(selectedOperator);
      }
    } catch (error: any) {
      console.error('Error asignando cliente:', error);
      setError('Error asignando cliente: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleUnassignClient = async (clientId: number) => {
    if (!selectedOperator) return;

    try {
      const response = await operatorPermissionsApi.unassignClient(
        selectedOperator.id,
        clientId
      );

      if (response.data.success) {
        setSuccess('Cliente desasignado exitosamente');
        // Recargar listas
        await handleManageClients(selectedOperator);
      }
    } catch (error: any) {
      console.error('Error desasignando cliente:', error);
      setError('Error desasignando cliente: ' + (error.response?.data?.error || error.message));
    }
  };

  const closeClientsModal = () => {
    setShowClientsModal(false);
    setSelectedOperator(null);
    setAvailableClients([]);
    setAssignedClients([]);
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '2rem' }}>
        <div className="spinner" />
        <span style={{ marginLeft: '0.5rem' }}>Cargando usuarios...</span>
      </div>
    );
  }

  return (
    <div>
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '2rem'
      }}>
        <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', margin: 0 }}>
          👥 {user.role === 'supervisor' ? 'Usuarios del Sistema' : 'Gestión de Usuarios'}
        </h2>
        {user.role === 'admin' && (
          <button 
            onClick={() => {
              resetForm();
              setShowForm(true);
            }}
            className="btn btn-primary"
          >
            + Crear Usuario
          </button>
        )}
      </div>

      {error && (
        <div className="error" style={{ marginBottom: '1rem' }}>
          {error}
        </div>
      )}

      {success && (
        <div className="success" style={{ marginBottom: '1rem' }}>
          {success}
        </div>
      )}

      {/* Búsqueda y Filtros */}
      <div className="card" style={{ marginBottom: '2rem' }}>
        <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1rem' }}>
          🔍 Buscar y Filtrar Usuarios
        </h3>
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
          gap: '1rem',
          alignItems: 'end'
        }}>
          <div className="form-group">
            <label className="form-label">Buscar por nombre o email</label>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="form-input"
              placeholder="Escribir para buscar..."
            />
          </div>
          <div className="form-group">
            <label className="form-label">Filtrar por rol</label>
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="form-input"
            >
              <option value="todos">Todos los roles</option>
              {roles.map(role => (
                <option key={role.value} value={role.value}>
                  {role.label}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <button 
              onClick={() => {
                setSearchTerm('');
                setRoleFilter('todos');
              }}
              style={{
                padding: '0.5rem 1rem',
                backgroundColor: '#6b7280',
                color: 'white',
                border: 'none',
                borderRadius: '0.25rem',
                cursor: 'pointer'
              }}
            >
              🗑️ Limpiar filtros
            </button>
          </div>
        </div>
        <div style={{ 
          marginTop: '1rem', 
          fontSize: '0.875rem', 
          color: '#6b7280' 
        }}>
          Mostrando {filteredUsers.length} de {users.length} usuarios
        </div>
      </div>

      {/* Formulario - Solo para admin */}
      {showForm && user.role === 'admin' && (
        <div className="card" style={{ marginBottom: '2rem' }}>
          <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1rem' }}>
            {editingUser ? 'Editar Usuario' : 'Crear Nuevo Usuario'}
          </h3>
          
          <form onSubmit={handleSubmit}>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
              gap: '1rem',
              marginBottom: '1rem'
            }}>
              <div className="form-group">
                <label className="form-label">Nombre Completo *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="form-input"
                  required
                  placeholder="Nombre completo del usuario"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Email *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="form-input"
                  required
                  placeholder="email@ejemplo.com"
                />
              </div>

              <div className="form-group">
                <label className="form-label">Username (para login)</label>
                <input
                  type="text"
                  value={formData.username || ''}
                  onChange={(e) => setFormData({...formData, username: e.target.value})}
                  className="form-input"
                  placeholder="Nombre para ingresar al sistema (ej: grido, admin, etc.)"
                />
                <small style={{ color: '#666', fontSize: '0.85rem' }}>
                  Opcional. Si no se especifica, se usará el email para login.
                </small>
              </div>

              {!editingUser && (
                <div className="form-group">
                  <label className="form-label">Contraseña *</label>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    className="form-input"
                    required
                    placeholder="Mínimo 6 caracteres"
                    minLength={6}
                  />
                </div>
              )}

              {editingUser && (
                <div className="form-group">
                  <label className="form-label">Contraseña Actual</label>
                  <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                    <input
                      type={showPassword ? "text" : "password"}
                      value={formData.password}
                      onChange={(e) => setFormData({...formData, password: e.target.value})}
                      className="form-input"
                      placeholder={changePassword ? "Escribir nueva contraseña (min 6 caracteres)" : "No se va a cambiar la contraseña"}
                      style={{ flex: 1 }}
                      readOnly={!changePassword}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      style={{
                        padding: '0.5rem',
                        backgroundColor: '#6b7280',
                        color: 'white',
                        border: 'none',
                        borderRadius: '0.25rem',
                        cursor: 'pointer'
                      }}
                    >
                      {showPassword ? '🙈' : '👁️'}
                    </button>
                    <button
                      type="button"
                      onClick={generatePassword}
                      style={{
                        padding: '0.5rem',
                        backgroundColor: '#10b981',
                        color: 'white',
                        border: 'none',
                        borderRadius: '0.25rem',
                        cursor: 'pointer',
                        fontSize: '0.75rem'
                      }}
                    >
                      Generar
                    </button>
                  </div>
                  <label className="form-label" style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                    <input
                      type="checkbox"
                      checked={changePassword}
                      onChange={(e) => {
                        setChangePassword(e.target.checked);
                        if (!e.target.checked) {
                          // Si desmarca, limpiar el campo de contraseña
                          setFormData(prev => ({...prev, password: ''}));
                        }
                      }}
                      style={{ marginRight: '0.5rem' }}
                    />
                    Cambiar Contraseña
                  </label>
                </div>
              )}

              <div className="form-group">
                <label className="form-label">Rol *</label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({...formData, role: e.target.value})}
                  className="form-input"
                  required
                >
                  {roles.map(role => (
                    <option key={role.value} value={role.value}>
                      {role.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Campos adicionales para clientes */}
            {formData.role === 'cliente' && (
              <div style={{ 
                marginBottom: '1rem',
                padding: '1rem',
                backgroundColor: '#f0f9ff',
                borderRadius: '0.5rem',
                border: '1px solid #0ea5e9'
              }}>
                <h4 style={{ fontSize: '1rem', fontWeight: '500', marginBottom: '1rem', color: '#0369a1' }}>
                  📋 Información de Empresa (Cliente)
                </h4>
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
                  gap: '1rem'
                }}>
                  <div className="form-group">
                    <label className="form-label">Nombre de Empresa</label>
                    <input
                      type="text"
                      value={formData.companyName}
                      onChange={(e) => setFormData({...formData, companyName: e.target.value})}
                      className="form-input"
                      placeholder="Nombre de la empresa"
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Teléfono</label>
                    <input
                      type="tel"
                      value={formData.companyPhone}
                      onChange={(e) => setFormData({...formData, companyPhone: e.target.value})}
                      className="form-input"
                      placeholder="(11) 1234-5678"
                    />
                  </div>

                  <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                    <label className="form-label">Dirección</label>
                    <input
                      type="text"
                      value={formData.companyAddress}
                      onChange={(e) => setFormData({...formData, companyAddress: e.target.value})}
                      className="form-input"
                      placeholder="Dirección completa de la empresa"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Sistema simplificado para administradores de grupo */}
            {formData.role === 'cliente' && (
              <div style={{ 
                marginBottom: '1rem',
                padding: '1rem',
                backgroundColor: '#f0f9ff',
                borderRadius: '0.5rem',
                border: '1px solid #3b82f6'
              }}>
                <h4 style={{ fontSize: '1rem', fontWeight: '500', marginBottom: '1rem', color: '#1e40af' }}>
                  🏢 Administrador de Grupo
                </h4>
                <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '1rem' }}>
                  Si este usuario administra múltiples negocios, selecciona qué clientes puede ver y gestionar.
                </div>
                
                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'flex', alignItems: 'center', marginBottom: '1rem' }}>
                    <input
                      type="checkbox"
                      checked={formData.permissions.esAdministradorGrupo || false}
                      onChange={(e) => setFormData({
                        ...formData, 
                        permissions: { ...formData.permissions, esAdministradorGrupo: e.target.checked }
                      })}
                      style={{ marginRight: '0.5rem' }}
                    />
                    <strong>Es Administrador de Grupo</strong>
                  </label>
                </div>

                {formData.permissions.esAdministradorGrupo && (
                  <div>
                    <div style={{ 
                      padding: '0.75rem', 
                      backgroundColor: '#dbeafe', 
                      borderRadius: '0.25rem', 
                      marginBottom: '1rem'
                    }}>
                      <div style={{ fontSize: '0.875rem', color: '#1e40af', marginBottom: '0.5rem' }}>
                        <strong>Beneficios de Administrador de Grupo:</strong>
                      </div>
                      <ul style={{ fontSize: '0.75rem', color: '#374151', marginLeft: '1rem' }}>
                        <li>Ve auditorías de múltiples clientes</li>
                        <li>Descarga reportes consolidados</li>
                        <li>Estados de cuenta unificados</li>
                        <li>Gestión centralizada de facturación</li>
                      </ul>
                    </div>

                    <div className="form-group">
                      <label className="form-label">Nombre del Grupo</label>
                      <input
                        type="text"
                        value={formData.permissions.nombreGrupo || ''}
                        onChange={(e) => setFormData({
                          ...formData, 
                          permissions: { ...formData.permissions, nombreGrupo: e.target.value }
                        })}
                        className="form-input"
                        placeholder="Ej: Grupo Kioscos del Centro"
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">Clientes que Administra</label>
                      <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.5rem' }}>
                        Selecciona los clientes que este administrador puede ver y gestionar:
                      </div>
                      <ClientesSelectorComponent 
                        selectedClients={formData.permissions.clientesAdministrados || []}
                        onClientesChange={(clientes) => setFormData({
                          ...formData, 
                          permissions: { ...formData.permissions, clientesAdministrados: clientes }
                        })}
                      />
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Configuración de comisiones para ejecutivos */}
            {formData.role === 'ejecutivo' && (
              <div style={{
                marginBottom: '1rem',
                padding: '1rem',
                backgroundColor: '#fff7ed',
                borderRadius: '0.5rem',
                border: '1px solid #ea580c'
              }}>
                <h4 style={{ fontSize: '1rem', fontWeight: '500', marginBottom: '1rem', color: '#c2410c' }}>
                  💰 Configuración de Comisiones
                </h4>
                <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '1rem' }}>
                  Define las comisiones que ganará este ejecutivo por captación de clientes nuevos y por cobros regulares.
                </div>

                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                  gap: '1rem'
                }}>
                  {/* Comisión por captación (primer cobro) */}
                  <div style={{
                    padding: '1rem',
                    backgroundColor: '#fef3c7',
                    borderRadius: '0.5rem',
                    border: '1px solid #fbbf24'
                  }}>
                    <div style={{ fontSize: '0.875rem', fontWeight: '600', color: '#92400e', marginBottom: '0.75rem' }}>
                      🌟 Comisión por Captación
                    </div>
                    <div style={{ fontSize: '0.75rem', color: '#78716c', marginBottom: '0.75rem' }}>
                      Se aplica la primera vez que un cliente paga (cliente nuevo)
                    </div>
                    <div className="form-group" style={{ marginBottom: '0.5rem' }}>
                      <label className="form-label">Tipo de Comisión</label>
                      <select
                        value={formData.tipo_comision_captacion}
                        onChange={(e) => setFormData({...formData, tipo_comision_captacion: e.target.value})}
                        className="form-input"
                      >
                        <option value="porcentaje">Porcentaje (%)</option>
                        <option value="fijo">Monto Fijo ($)</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="form-label">
                        {formData.tipo_comision_captacion === 'porcentaje' ? 'Porcentaje (%)' : 'Monto Fijo ($)'}
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={formData.comision_captacion}
                        onChange={(e) => setFormData({...formData, comision_captacion: e.target.value})}
                        className="form-input"
                        placeholder={formData.tipo_comision_captacion === 'porcentaje' ? 'Ej: 10 para 10%' : 'Ej: 5000'}
                      />
                    </div>
                  </div>

                  {/* Comisión por cobro normal */}
                  <div style={{
                    padding: '1rem',
                    backgroundColor: '#ecfdf5',
                    borderRadius: '0.5rem',
                    border: '1px solid #34d399'
                  }}>
                    <div style={{ fontSize: '0.875rem', fontWeight: '600', color: '#065f46', marginBottom: '0.75rem' }}>
                      💵 Comisión por Cobro Regular
                    </div>
                    <div style={{ fontSize: '0.75rem', color: '#78716c', marginBottom: '0.75rem' }}>
                      Se aplica a partir del segundo cobro y en adelante
                    </div>
                    <div className="form-group" style={{ marginBottom: '0.5rem' }}>
                      <label className="form-label">Tipo de Comisión</label>
                      <select
                        value={formData.tipo_comision_cobro}
                        onChange={(e) => setFormData({...formData, tipo_comision_cobro: e.target.value})}
                        className="form-input"
                      >
                        <option value="porcentaje">Porcentaje (%)</option>
                        <option value="fijo">Monto Fijo ($)</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="form-label">
                        {formData.tipo_comision_cobro === 'porcentaje' ? 'Porcentaje (%)' : 'Monto Fijo ($)'}
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={formData.comision_cobro}
                        onChange={(e) => setFormData({...formData, comision_cobro: e.target.value})}
                        className="form-input"
                        placeholder={formData.tipo_comision_cobro === 'porcentaje' ? 'Ej: 5 para 5%' : 'Ej: 2000'}
                      />
                    </div>
                  </div>
                </div>

                <div style={{ marginTop: '1rem', padding: '0.75rem', backgroundColor: '#dbeafe', borderRadius: '0.25rem', fontSize: '0.75rem', color: '#1e40af' }}>
                  💡 <strong>Ejemplo:</strong> Si captación es 10% y cobro regular es 5%, cuando el ejecutivo cobra $50.000 de un cliente nuevo gana $5.000, pero en los meses siguientes gana $2.500 por cobrar al mismo cliente.
                </div>
              </div>
            )}

            {/* Sistema de permisos granulares - Solo para roles no-cliente */}
            {formData.role !== 'cliente' && (
              <div style={{ 
                marginBottom: '1rem',
                padding: '1rem',
                backgroundColor: '#fef9e7',
                borderRadius: '0.5rem',
                border: '1px solid #f59e0b'
              }}>
                <h4 style={{ fontSize: '1rem', fontWeight: '500', marginBottom: '1rem', color: '#92400e' }}>
                  🔐 Permisos Granulares
                </h4>
                <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '1rem' }}>
                  Personaliza los permisos específicos para este usuario. Los permisos marcados anularán los permisos por defecto del rol.
                </div>
              
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.5rem' }}>
                {/* Permisos de auditorías */}
                <div>
                  <strong style={{ fontSize: '0.875rem', color: '#374151' }}>Auditorías:</strong>
                  <div style={{ marginTop: '0.5rem' }}>
                    <label style={{ display: 'block', fontSize: '0.75rem', marginBottom: '0.25rem' }}>
                      <input
                        type="checkbox"
                        checked={formData.permissions.audit_view || false}
                        onChange={(e) => setFormData({
                          ...formData, 
                          permissions: { ...formData.permissions, audit_view: e.target.checked }
                        })}
                        style={{ marginRight: '0.5rem' }}
                      />
                      Ver auditorías
                    </label>
                    <label style={{ display: 'block', fontSize: '0.75rem', marginBottom: '0.25rem' }}>
                      <input
                        type="checkbox"
                        checked={formData.permissions.audit_correct || false}
                        onChange={(e) => setFormData({
                          ...formData, 
                          permissions: { ...formData.permissions, audit_correct: e.target.checked }
                        })}
                        style={{ marginRight: '0.5rem' }}
                      />
                      Corregir auditorías
                    </label>
                    <label style={{ display: 'block', fontSize: '0.75rem' }}>
                      <input
                        type="checkbox"
                        checked={formData.permissions.audit_create || false}
                        onChange={(e) => setFormData({
                          ...formData, 
                          permissions: { ...formData.permissions, audit_create: e.target.checked }
                        })}
                        style={{ marginRight: '0.5rem' }}
                      />
                      Crear auditorías
                    </label>
                  </div>
                </div>

                {/* Permisos de finanzas */}
                <div>
                  <strong style={{ fontSize: '0.875rem', color: '#374151' }}>Finanzas:</strong>
                  <div style={{ marginTop: '0.5rem' }}>
                    <label style={{ display: 'block', fontSize: '0.75rem', marginBottom: '0.25rem' }}>
                      <input
                        type="checkbox"
                        checked={formData.permissions.finance_view || false}
                        onChange={(e) => setFormData({
                          ...formData, 
                          permissions: { ...formData.permissions, finance_view: e.target.checked }
                        })}
                        style={{ marginRight: '0.5rem' }}
                      />
                      Ver finanzas
                    </label>
                    <label style={{ display: 'block', fontSize: '0.75rem', marginBottom: '0.25rem' }}>
                      <input
                        type="checkbox"
                        checked={formData.permissions.finance_manage || false}
                        onChange={(e) => setFormData({
                          ...formData, 
                          permissions: { ...formData.permissions, finance_manage: e.target.checked }
                        })}
                        style={{ marginRight: '0.5rem' }}
                      />
                      Gestionar finanzas
                    </label>
                    <label style={{ display: 'block', fontSize: '0.75rem', marginBottom: '0.25rem' }}>
                      <input
                        type="checkbox"
                        checked={formData.permissions.puedeVerSueldo || false}
                        onChange={(e) => setFormData({
                          ...formData, 
                          permissions: { ...formData.permissions, puedeVerSueldo: e.target.checked }
                        })}
                        style={{ marginRight: '0.5rem' }}
                      />
                      💰 Puede ver su propio sueldo
                    </label>
                  </div>
                </div>

                {/* Permisos de usuarios */}
                <div>
                  <strong style={{ fontSize: '0.875rem', color: '#374151' }}>Usuarios:</strong>
                  <div style={{ marginTop: '0.5rem' }}>
                    <label style={{ display: 'block', fontSize: '0.75rem', marginBottom: '0.25rem' }}>
                      <input
                        type="checkbox"
                        checked={formData.permissions.user_view || false}
                        onChange={(e) => setFormData({
                          ...formData, 
                          permissions: { ...formData.permissions, user_view: e.target.checked }
                        })}
                        style={{ marginRight: '0.5rem' }}
                      />
                      Ver usuarios
                    </label>
                    <label style={{ display: 'block', fontSize: '0.75rem' }}>
                      <input
                        type="checkbox"
                        checked={formData.permissions.user_manage || false}
                        onChange={(e) => setFormData({
                          ...formData, 
                          permissions: { ...formData.permissions, user_manage: e.target.checked }
                        })}
                        style={{ marginRight: '0.5rem' }}
                      />
                      Gestionar usuarios
                    </label>
                  </div>
                </div>

                {/* Permisos de clientes */}
                <div>
                  <strong style={{ fontSize: '0.875rem', color: '#374151' }}>Clientes:</strong>
                  <div style={{ marginTop: '0.5rem' }}>
                    <label style={{ display: 'block', fontSize: '0.75rem', marginBottom: '0.25rem' }}>
                      <input
                        type="checkbox"
                        checked={formData.permissions.client_view || false}
                        onChange={(e) => setFormData({
                          ...formData, 
                          permissions: { ...formData.permissions, client_view: e.target.checked }
                        })}
                        style={{ marginRight: '0.5rem' }}
                      />
                      Ver clientes
                    </label>
                    <label style={{ display: 'block', fontSize: '0.75rem' }}>
                      <input
                        type="checkbox"
                        checked={formData.permissions.client_manage || false}
                        onChange={(e) => setFormData({
                          ...formData, 
                          permissions: { ...formData.permissions, client_manage: e.target.checked }
                        })}
                        style={{ marginRight: '0.5rem' }}
                      />
                      Gestionar clientes
                    </label>
                  </div>
                </div>
              </div>

              <div style={{ marginTop: '1rem', padding: '0.75rem', backgroundColor: '#fef3c7', borderRadius: '0.25rem', fontSize: '0.75rem', color: '#92400e' }}>
                💡 <strong>Ejemplo:</strong> Un supervisor que puede ver auditorías pero no corregirlas, o un operador que puede corregir auditorías específicas.
              </div>
              </div>
            )}

            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
              <button 
                type="button" 
                onClick={resetForm}
                className="btn btn-secondary"
              >
                Cancelar
              </button>
              <button 
                type="submit"
                className="btn btn-primary"
              >
                {editingUser ? 'Actualizar' : 'Crear'} Usuario
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Lista de usuarios */}
      <div className="card">
        <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1rem' }}>
          Usuarios del Sistema ({filteredUsers.length})
        </h3>

        {filteredUsers.length > 0 && (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ 
              width: '100%', 
              borderCollapse: 'collapse',
              fontSize: '0.875rem'
            }}>
              <thead>
                <tr style={{ 
                  borderBottom: '2px solid #e5e7eb',
                  backgroundColor: '#f9fafb'
                }}>
                  <th style={{ padding: '0.75rem', textAlign: 'left' }}>Usuario</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left' }}>Email</th>
                  <th style={{ padding: '0.75rem', textAlign: 'center' }}>Rol</th>
                  <th style={{ padding: '0.75rem', textAlign: 'center' }}>Estado</th>
                  {(['admin', 'contable'].includes(user.role)) && (
                    <th style={{ padding: '0.75rem', textAlign: 'center' }}>💰 Sueldo</th>
                  )}
                  <th style={{ padding: '0.75rem', textAlign: 'center' }}>Último Login</th>
                  <th style={{ padding: '0.75rem', textAlign: 'center' }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((userRecord) => {
                  const roleColor = getRoleColor(userRecord.role);
                  return (
                    <tr key={userRecord.id} style={{ borderBottom: '1px solid #e5e7eb' }}>
                      <td style={{ padding: '0.75rem' }}>
                        <div>
                          <div style={{ fontWeight: '500' }}>{userRecord.name}</div>
                          <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                            ID: {userRecord.id}
                          </div>
                        </div>
                      </td>
                      <td style={{ padding: '0.75rem' }}>{userRecord.email}</td>
                      <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                        <span style={{
                          padding: '0.25rem 0.5rem',
                          borderRadius: '0.25rem',
                          fontSize: '0.75rem',
                          fontWeight: '500',
                          backgroundColor: roleColor.bg,
                          color: roleColor.text
                        }}>
                          {userRecord.role}
                        </span>
                      </td>
                      <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                        <span style={{
                          padding: '0.25rem 0.5rem',
                          borderRadius: '0.25rem',
                          fontSize: '0.75rem',
                          fontWeight: '500',
                          backgroundColor: userRecord.isActive ? '#d1fae5' : '#fee2e2',
                          color: userRecord.isActive ? '#065f46' : '#991b1b'
                        }}>
                          {userRecord.isActive ? 'Activo' : 'Inactivo'}
                        </span>
                      </td>
                      {(['admin', 'contable'].includes(user.role)) && (
                        <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                          {['operador', 'supervisor', 'admin', 'contable'].includes(userRecord.role) ? (
                            usersSueldos[userRecord.id] ? (
                              <div style={{ fontSize: '0.75rem' }}>
                                <div style={{
                                  fontWeight: '600',
                                  color: '#059669',
                                  marginBottom: '0.25rem'
                                }}>
                                  ${usersSueldos[userRecord.id].sueldoFinal.toLocaleString()}
                                </div>
                                <div style={{
                                  fontSize: '0.7rem',
                                  color: '#6b7280',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  gap: '0.25rem'
                                }}>
                                  <div style={{
                                    width: '40px',
                                    height: '6px',
                                    backgroundColor: '#e5e7eb',
                                    borderRadius: '3px',
                                    overflow: 'hidden'
                                  }}>
                                    <div style={{
                                      width: `${Math.min(parseFloat(usersSueldos[userRecord.id].porcentajeObjetivo) || 0, 100)}%`,
                                      height: '100%',
                                      backgroundColor: parseFloat(usersSueldos[userRecord.id].porcentajeObjetivo || 0) >= 80 ? '#10b981' :
                                                      parseFloat(usersSueldos[userRecord.id].porcentajeObjetivo || 0) >= 50 ? '#f59e0b' : '#ef4444'
                                    }} />
                                  </div>
                                  {parseFloat(usersSueldos[userRecord.id].porcentajeObjetivo || 0).toFixed(0)}%
                                </div>
                              </div>
                            ) : (
                              <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
                                Cargando...
                              </span>
                            )
                          ) : (
                            <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
                              N/A
                            </span>
                          )}
                        </td>
                      )}
                      <td style={{ padding: '0.75rem', textAlign: 'center', fontSize: '0.75rem' }}>
                        {userRecord.lastLogin ?
                          new Date(userRecord.lastLogin).toLocaleDateString() :
                          'Nunca'
                        }
                      </td>
                      <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                        <div style={{ display: 'flex', gap: '0.25rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                          <button
                            onClick={() => setShowUserDashboard(userRecord)}
                            style={{
                              padding: '0.25rem 0.5rem',
                              backgroundColor: '#8b5cf6',
                              color: 'white',
                              border: 'none',
                              borderRadius: '0.25rem',
                              fontSize: '0.75rem',
                              cursor: 'pointer'
                            }}
                            title="Ver dashboard completo del usuario"
                          >
                            📊 Dashboard
                          </button>
                          <button
                            onClick={() => {
                              setShowUserDetail(userRecord);
                              loadUserMessages(userRecord.id);
                              loadUserSueldoAndWorkData(userRecord);
                            }}
                            style={{
                              padding: '0.25rem 0.5rem',
                              backgroundColor: '#10b981',
                              color: 'white',
                              border: 'none',
                              borderRadius: '0.25rem',
                              fontSize: '0.75rem',
                              cursor: 'pointer'
                            }}
                            title="Ver detalles y enviar mensajes"
                          >
                            👁️ Detalles
                          </button>
                          {user.role === 'admin' && (
                            <button
                              onClick={() => handleEdit(userRecord)}
                              style={{
                                padding: '0.25rem 0.5rem',
                                backgroundColor: '#3b82f6',
                                color: 'white',
                                border: 'none',
                                borderRadius: '0.25rem',
                                fontSize: '0.75rem',
                                cursor: 'pointer'
                              }}
                            >
                              ✏️ Editar
                            </button>
                          )}
                          {user.role === 'admin' && userRecord.id !== user.id && (
                            <button
                              onClick={() => handleToggleActive(userRecord.id, userRecord.isActive)}
                              style={{
                                padding: '0.25rem 0.5rem',
                                backgroundColor: userRecord.isActive ? '#ef4444' : '#10b981',
                                color: 'white',
                                border: 'none',
                                borderRadius: '0.25rem',
                                fontSize: '0.75rem',
                                cursor: 'pointer'
                              }}
                            >
                              {userRecord.isActive ? 'Desactivar' : 'Activar'}
                            </button>
                          )}
                          {user.role === 'admin' && ['operador', 'supervisor'].includes(userRecord.role) && (
                            <button
                              onClick={() => handleManageClients(userRecord)}
                              style={{
                                padding: '0.25rem 0.5rem',
                                backgroundColor: '#8b5cf6',
                                color: 'white',
                                border: 'none',
                                borderRadius: '0.25rem',
                                fontSize: '0.75rem',
                                cursor: 'pointer'
                              }}
                            >
                              👥 Clientes
                            </button>
                          )}
                          {user.role === 'admin' && userRecord.id !== user.id && (
                            <button
                              onClick={() => handleDelete(userRecord)}
                              style={{
                                padding: '0.25rem 0.5rem',
                                backgroundColor: '#dc2626',
                                color: 'white',
                                border: 'none',
                                borderRadius: '0.25rem',
                                fontSize: '0.75rem',
                                cursor: 'pointer'
                              }}
                            >
                              🗑️ Eliminar
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal de detalle del usuario */}
      {showUserDetail && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '1rem'
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '0.5rem',
            padding: '2rem',
            maxWidth: '900px',
            width: '100%',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '2rem'
            }}>
              <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', margin: 0 }}>
                👤 Detalle de Usuario: {showUserDetail.name}
              </h2>
              <button
                onClick={() => {
                  setShowUserDetail(null);
                  setMessageContent('');
                  setUserMessages([]);
                }}
                style={{
                  padding: '0.5rem',
                  backgroundColor: '#ef4444',
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.25rem',
                  cursor: 'pointer'
                }}
              >
                ✕ Cerrar
              </button>
            </div>

            {/* Información básica */}
            <div className="card" style={{ marginBottom: '2rem' }}>
              <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1rem' }}>
                📋 Información Básica
              </h3>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '1rem'
              }}>
                <div>
                  <strong>Nombre:</strong> {showUserDetail.name}
                </div>
                <div>
                  <strong>Email:</strong> {showUserDetail.email}
                </div>
                <div>
                  <strong>Rol:</strong> 
                  <span style={{
                    marginLeft: '0.5rem',
                    padding: '0.25rem 0.5rem',
                    borderRadius: '0.25rem',
                    fontSize: '0.75rem',
                    fontWeight: '500',
                    backgroundColor: getRoleColor(showUserDetail.role).bg,
                    color: getRoleColor(showUserDetail.role).text
                  }}>
                    {showUserDetail.role}
                  </span>
                </div>
                <div>
                  <strong>Estado:</strong>
                  <span style={{
                    marginLeft: '0.5rem',
                    padding: '0.25rem 0.5rem',
                    borderRadius: '0.25rem',
                    fontSize: '0.75rem',
                    fontWeight: '500',
                    backgroundColor: showUserDetail.isActive ? '#d1fae5' : '#fee2e2',
                    color: showUserDetail.isActive ? '#065f46' : '#991b1b'
                  }}>
                    {showUserDetail.isActive ? 'Activo' : 'Inactivo'}
                  </span>
                </div>
                <div>
                  <strong>Último Login:</strong> {showUserDetail.lastLogin ?
                    new Date(showUserDetail.lastLogin).toLocaleString() :
                    'Nunca'
                  }
                  {showUserDetail.role === 'cliente' && (
                    <button
                      onClick={() => {
                        setShowLoginHistory(!showLoginHistory);
                        if (!showLoginHistory && loginHistory.length === 0) {
                          loadLoginHistory(showUserDetail.id);
                        }
                      }}
                      style={{
                        marginLeft: '1rem',
                        padding: '0.25rem 0.5rem',
                        fontSize: '0.75rem',
                        backgroundColor: '#3b82f6',
                        color: 'white',
                        border: 'none',
                        borderRadius: '0.25rem',
                        cursor: 'pointer'
                      }}
                    >
                      {showLoginHistory ? '🔼 Ocultar historial' : '🔽 Ver historial completo'}
                    </button>
                  )}
                </div>
                <div>
                  <strong>Registrado:</strong> {new Date(showUserDetail.createdAt).toLocaleString()}
                </div>
              </div>
            </div>

            {/* Historial de Logins - Solo para clientes */}
            {showUserDetail.role === 'cliente' && showLoginHistory && (
              <div className="card" style={{ marginBottom: '2rem' }}>
                <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1rem' }}>
                  📋 Historial de Conexiones
                </h3>
                {loadingLoginHistory ? (
                  <div style={{ textAlign: 'center', padding: '20px' }}>
                    <div className="spinner" />
                    <p style={{ margin: '8px 0 0 0', fontSize: '14px', color: '#6b7280' }}>
                      Cargando historial...
                    </p>
                  </div>
                ) : loginHistory.length === 0 ? (
                  <p style={{ color: '#6b7280', textAlign: 'center', padding: '1rem' }}>
                    No hay conexiones registradas
                  </p>
                ) : (
                  <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                      <thead>
                        <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                          <th style={{ padding: '0.75rem', textAlign: 'left' }}>Fecha y Hora</th>
                          <th style={{ padding: '0.75rem', textAlign: 'left' }}>IP</th>
                          <th style={{ padding: '0.75rem', textAlign: 'left' }}>Dispositivo</th>
                        </tr>
                      </thead>
                      <tbody>
                        {loginHistory.map((login, index) => (
                          <tr key={login.id} style={{
                            borderBottom: '1px solid #e5e7eb',
                            backgroundColor: index % 2 === 0 ? '#f9fafb' : 'white'
                          }}>
                            <td style={{ padding: '0.75rem' }}>
                              {new Date(login.loginTime).toLocaleString('es-AR', {
                                year: 'numeric',
                                month: '2-digit',
                                day: '2-digit',
                                hour: '2-digit',
                                minute: '2-digit',
                                second: '2-digit'
                              })}
                            </td>
                            <td style={{ padding: '0.75rem', fontFamily: 'monospace', fontSize: '0.75rem' }}>
                              {login.ipAddress || 'N/A'}
                            </td>
                            <td style={{ padding: '0.75rem', fontSize: '0.75rem', maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={login.userAgent}>
                              {login.userAgent ? login.userAgent.split(' ')[0] : 'N/A'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {/* Información de Sueldo y Control de Tiempo - Solo para empleados */}
            {['operador', 'supervisor', 'admin', 'contable'].includes(showUserDetail.role) && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '2rem' }}>
                
                {/* Control de Tiempo */}
                <div className="card">
                  <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1rem' }}>
                    ⏰ Control de Tiempo
                  </h3>
                  
                  {loadingUserDetails ? (
                    <div style={{ textAlign: 'center', padding: '20px' }}>
                      <div className="spinner" />
                      <p style={{ margin: '8px 0 0 0', fontSize: '14px', color: '#6b7280' }}>
                        Cargando datos de tiempo...
                      </p>
                    </div>
                  ) : workSessionData ? (
                    <div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', marginBottom: '16px' }}>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: '18px', fontWeight: '600', color: workSessionData.sesionActiva ? '#10b981' : '#6b7280' }}>
                            {workSessionData.horasActuales}h
                          </div>
                          <div style={{ fontSize: '12px', color: '#6b7280' }}>Horas Hoy</div>
                        </div>
                        
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: '18px', fontWeight: '600', color: '#3b82f6' }}>
                            {workSessionData.horasRegulares}h
                          </div>
                          <div style={{ fontSize: '12px', color: '#6b7280' }}>Regulares</div>
                        </div>
                        
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: '18px', fontWeight: '600', color: workSessionData.horasExtra > 0 ? '#f59e0b' : '#6b7280' }}>
                            {workSessionData.horasExtra}h
                          </div>
                          <div style={{ fontSize: '12px', color: '#6b7280' }}>Extra</div>
                        </div>
                      </div>

                      {/* Alerta de tiempo excesivo */}
                      {(() => {
                        const alert = getTimeAlert(workSessionData.horasActuales);
                        if (alert) {
                          return (
                            <div style={{
                              marginBottom: '12px',
                              padding: '8px 12px',
                              backgroundColor: alert.level === 'critical' ? '#fef2f2' : alert.level === 'warning' ? '#fffbeb' : '#f0f9ff',
                              border: `1px solid ${alert.level === 'critical' ? '#fecaca' : alert.level === 'warning' ? '#fed7aa' : '#bfdbfe'}`,
                              borderRadius: '6px',
                              fontSize: '14px',
                              color: alert.level === 'critical' ? '#dc2626' : alert.level === 'warning' ? '#d97706' : '#2563eb',
                              fontWeight: '500'
                            }}>
                              {alert.message}
                            </div>
                          );
                        }
                        return null;
                      })()}

                      <div style={{
                        padding: '8px',
                        backgroundColor: workSessionData.sesionActiva ? '#dcfce7' : '#f3f4f6',
                        borderRadius: '4px',
                        textAlign: 'center',
                        fontSize: '12px',
                        color: workSessionData.sesionActiva ? '#166534' : '#6b7280'
                      }}>
                        {workSessionData.sesionActiva ? '🟢 Sesión Activa' : '⚫ Sin Sesión'}
                      </div>

                      {workSessionData.message && (
                        <p style={{ margin: '8px 0 0 0', fontSize: '12px', color: '#6b7280', textAlign: 'center' }}>
                          {workSessionData.message}
                        </p>
                      )}
                    </div>
                  ) : (
                    <div style={{ textAlign: 'center', padding: '20px', color: '#6b7280' }}>
                      <p style={{ margin: 0 }}>No hay datos de control de tiempo disponibles</p>
                    </div>
                  )}
                </div>

                {/* Información de Sueldo - Solo admin/contable o propio usuario */}
                {(user.role === 'admin' || user.role === 'contable' || showUserDetail.id === user.id) && (
                <div className="card">
                  <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1rem' }}>
                    💰 Información del Sueldo
                  </h3>
                  
                  {loadingUserDetails ? (
                    <div style={{ textAlign: 'center', padding: '20px' }}>
                      <div className="spinner" />
                      <p style={{ margin: '8px 0 0 0', fontSize: '14px', color: '#6b7280' }}>
                        Cargando datos de sueldo...
                      </p>
                    </div>
                  ) : sueldoData ? (
                    <div>
                      {/* Barra de progreso */}
                      <div style={{ marginBottom: '16px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                          <span style={{ fontSize: '14px', color: '#6b7280' }}>Progreso del objetivo</span>
                          <span style={{ 
                            fontSize: '14px', 
                            fontWeight: '600',
                            color: getProgressColor(sueldoData.sueldo.porcentajeObjetivo || 0)
                          }}>
                            {parseFloat(sueldoData.sueldo.porcentajeObjetivo || 0).toFixed(1)}%
                          </span>
                        </div>
                        <div style={{
                          width: '100%',
                          height: '8px',
                          backgroundColor: '#e5e7eb',
                          borderRadius: '4px',
                          overflow: 'hidden'
                        }}>
                          <div style={{
                            width: `${Math.min(parseFloat(sueldoData.sueldo.porcentajeObjetivo) || 0, 100)}%`,
                            height: '100%',
                            backgroundColor: getProgressColor(sueldoData.sueldo.porcentajeObjetivo || 0),
                            transition: 'all 0.3s ease'
                          }} />
                        </div>
                      </div>

                      {/* Detalles del sueldo */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span style={{ fontSize: '14px', color: '#6b7280' }}>Sueldo referencia:</span>
                          <span style={{ fontSize: '14px', fontWeight: '500' }}>
                            {formatMoney(sueldoData.detalle.sueldoReferencia)}
                          </span>
                        </div>
                        
                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                          <span style={{ fontSize: '14px', color: '#6b7280' }}>Auditorías realizadas:</span>
                          <span style={{ fontSize: '14px', fontWeight: '500', color: '#10b981' }}>
                            {sueldoData.detalle.auditorias.hechas}
                          </span>
                        </div>

                        {showUserDetail.role === 'supervisor' && (
                          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span style={{ fontSize: '14px', color: '#6b7280' }}>Correcciones:</span>
                            <span style={{ fontSize: '14px', fontWeight: '500', color: '#3b82f6' }}>
                              {sueldoData.sueldo.correccionesHechas || 0}
                            </span>
                          </div>
                        )}
                        
                        {/* Mostrar información de pagos si hay pagos realizados */}
                        {sueldoData.detalle.totalPagado > 0 && (
                          <div style={{ 
                            paddingTop: '8px',
                            borderTop: '1px solid #e5e7eb',
                            marginBottom: '8px'
                          }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                              <span style={{ fontSize: '14px', color: '#6b7280' }}>Sueldo original:</span>
                              <span style={{ fontSize: '14px', fontWeight: '500' }}>
                                {formatMoney(sueldoData.detalle.sueldoOriginal)}
                              </span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                              <span style={{ fontSize: '14px', color: '#ef4444' }}>Ya pagado:</span>
                              <span style={{ fontSize: '14px', fontWeight: '500', color: '#ef4444' }}>
                                -{formatMoney(sueldoData.detalle.totalPagado)}
                              </span>
                            </div>
                          </div>
                        )}

                        <div style={{ 
                          display: 'flex', 
                          justifyContent: 'space-between',
                          paddingTop: '8px',
                          borderTop: '1px solid #e5e7eb'
                        }}>
                          <span style={{ fontSize: '14px', fontWeight: '600', color: '#1f2937' }}>
                            {sueldoData.detalle.totalPagado > 0 ? 'Sueldo pendiente:' : 'Sueldo final:'}
                          </span>
                          <span style={{ 
                            fontSize: '16px', 
                            fontWeight: '700', 
                            color: sueldoData.detalle.totalPagado > 0 ? '#059669' : '#1f2937' 
                          }}>
                            {formatMoney(sueldoData.detalle.total)}
                          </span>
                        </div>
                        
                        {/* Botones Pagar/Revertir */}
                        <div style={{ marginTop: '16px', textAlign: 'center', display: 'flex', gap: '10px', justifyContent: 'center' }}>
                          {sueldoData.detalle.pendiente > 0 ? (
                            <button
                            onClick={async () => {
                              const monto = prompt(
                                `💰 Pagar sueldo a ${showUserDetail.name}\n\nSueldo completo: ${formatMoney(sueldoData.detalle.total)}\n\nIngresa el monto a pagar (deja vacío para pago completo):`,
                                sueldoData.detalle.total.toString()
                              );
                              
                              if (monto !== null) {
                                try {
                                  const montoPago = parseFloat(monto) || sueldoData.detalle.total;
                                  const esPagoCompleto = montoPago >= sueldoData.detalle.total;
                                  
                                  // Registrar pago en el sistema financiero
                                  const pagoData = {
                                    operadorId: showUserDetail.id,
                                    operadorNombre: showUserDetail.name,
                                    monto: montoPago,
                                    concepto: `Pago ${esPagoCompleto ? 'completo' : 'parcial'} de sueldo - ${showUserDetail.name}`,
                                    notas: `Pago ${esPagoCompleto ? 'completo' : 'parcial'} registrado desde dashboard admin`
                                  };
                                  
                                  // Usar el endpoint real que actualiza el modelo Sueldo
                                  const response = await api.post('/api/sueldos/pagar', {
                                    sueldoId: sueldoData.id, // ID del sueldo calculado
                                    montoPago: montoPago,
                                    tipoPago: esPagoCompleto ? 'Pago Total' : 'Pago Parcial',
                                    concepto: `Pago ${esPagoCompleto ? 'completo' : 'parcial'} de sueldo - ${showUserDetail.name}`,
                                    notas: `Pago ${esPagoCompleto ? 'completo' : 'parcial'} registrado desde dashboard admin`
                                  });
                                  
                                  if (response.data.success) {
                                    alert(
                                      `✅ ${esPagoCompleto ? 'Pago completo' : 'Pago parcial'} registrado en finanzas:\n\n` +
                                      `👤 Empleado: ${showUserDetail.name}\n` +
                                      `💰 Monto: ${formatMoney(montoPago)}\n` +
                                      `📅 Fecha: ${new Date().toLocaleDateString()}\n` +
                                      `🆔 ID Movimiento: ${response.data.data.movimientoId}\n\n` +
                                      `${esPagoCompleto ? '🎉 Sueldo pagado completamente' : `⚠️ Resta: ${formatMoney(sueldoData.detalle.total - montoPago)}`}\n\n` +
                                      `💼 El egreso fue registrado en el sistema financiero.`
                                    );
                                  } else {
                                    throw new Error(response.data.message || 'Error registrando pago');
                                  }
                                } catch (error: any) {
                                  console.error('Error registrando pago:', error);
                                  alert(`❌ Error registrando el pago:\n${error.response?.data?.error || error.message || 'Error desconocido'}\n\nPor favor intenta nuevamente.`);
                                }
                              }
                            }}
                            style={{
                              backgroundColor: '#10b981',
                              color: 'white',
                              border: 'none',
                              padding: '10px 20px',
                              borderRadius: '6px',
                              fontSize: '14px',
                              fontWeight: '600',
                              cursor: 'pointer',
                              transition: 'background-color 0.2s'
                            }}
                            onMouseOver={(e) => e.target.style.backgroundColor = '#059669'}
                            onMouseOut={(e) => e.target.style.backgroundColor = '#10b981'}
                          >
                            💰 Pagar Sueldo
                          </button>
                          ) : sueldoData.detalle.totalPagado > 0 ? (
                            <button
                              onClick={async () => {
                                const motivo = prompt(`¿Por qué razón reviertes el pago del sueldo de ${showUserDetail.name}?`);
                                if (!motivo) return;
                                
                                if (!confirm(`¿Confirmas revertir el pago del sueldo de ${showUserDetail.name}? Esto creará un egreso compensatorio.`)) return;
                                
                                try {
                                  // Usar el endpoint real de reversión
                                  const response = await api.post("/api/sueldos/revertir", {
                                    operadorId: showUserDetail.id,
                                    sueldoId: sueldoData.id,
                                    motivo: motivo,
                                    monto: sueldoData.detalle.totalPagado
                                  });
                                  
                                  alert(`🔄 Pago revertido: Sueldo de ${showUserDetail.name} revertido - Motivo: ${motivo}`);
                                  
                                  // Recargar datos del usuario
                                  loadUserDetails(showUserDetail.id);
                                  
                                } catch (error: any) {
                                  console.error('Error revirtiendo pago:', error);
                                  alert(`❌ Error revirtiendo pago: ${error.response?.data?.error || error.message || 'Error desconocido'}`);
                                }
                              }}
                              style={{
                                backgroundColor: '#f59e0b',
                                color: 'white',
                                border: 'none',
                                padding: '10px 20px',
                                borderRadius: '6px',
                                fontSize: '14px',
                                fontWeight: '600',
                                cursor: 'pointer',
                                transition: 'background-color 0.2s'
                              }}
                              onMouseOver={(e) => e.target.style.backgroundColor = '#d97706'}
                              onMouseOut={(e) => e.target.style.backgroundColor = '#f59e0b'}
                            >
                              🔄 Revertir Pago
                            </button>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div style={{ textAlign: 'center', padding: '20px', color: '#6b7280' }}>
                      <p style={{ margin: 0 }}>No hay información de sueldo configurada</p>
                    </div>
                  )}
                </div>
                )}
              </div>
            )}

            {/* Sistema de mensajes */}
            <div className="card">
              <h3 style={{ fontSize: '1.25rem', fontWeight: '600', marginBottom: '1rem' }}>
                💬 Mensajes Administrativos
              </h3>
              
              {/* Formulario para enviar mensaje */}
              <div style={{ 
                padding: '1rem', 
                backgroundColor: '#f0f9ff', 
                borderRadius: '0.5rem',
                marginBottom: '1rem'
              }}>
                <h4 style={{ fontSize: '1rem', fontWeight: '500', marginBottom: '0.5rem' }}>
                  ✉️ Enviar Mensaje
                </h4>
                <textarea
                  value={messageContent}
                  onChange={(e) => setMessageContent(e.target.value)}
                  placeholder="Escribir mensaje al usuario..."
                  style={{
                    width: '100%',
                    minHeight: '80px',
                    padding: '0.5rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '0.25rem',
                    marginBottom: '0.5rem',
                    resize: 'vertical'
                  }}
                />
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button
                    onClick={() => handleSendMessage('mensaje')}
                    disabled={!messageContent.trim()}
                    style={{
                      padding: '0.5rem 1rem',
                      backgroundColor: messageContent.trim() ? '#10b981' : '#9ca3af',
                      color: 'white',
                      border: 'none',
                      borderRadius: '0.25rem',
                      cursor: messageContent.trim() ? 'pointer' : 'not-allowed'
                    }}
                  >
                    📤 Enviar Mensaje
                  </button>
                  <button
                    onClick={() => handleSendMessage('alerta')}
                    disabled={!messageContent.trim()}
                    style={{
                      padding: '0.5rem 1rem',
                      backgroundColor: messageContent.trim() ? '#f59e0b' : '#9ca3af',
                      color: 'white',
                      border: 'none',
                      borderRadius: '0.25rem',
                      cursor: messageContent.trim() ? 'pointer' : 'not-allowed'
                    }}
                  >
                    🚨 Enviar Alerta
                  </button>
                </div>
              </div>

              {/* Historial de mensajes */}
              <div>
                <h4 style={{ fontSize: '1rem', fontWeight: '500', marginBottom: '0.5rem' }}>
                  📜 Historial de Mensajes
                </h4>
                
                {loadingMessages ? (
                  <div style={{ textAlign: 'center', padding: '1rem' }}>
                    Cargando mensajes...
                  </div>
                ) : userMessages.length > 0 ? (
                  <div style={{ 
                    maxHeight: '300px', 
                    overflowY: 'auto',
                    border: '1px solid #e5e7eb',
                    borderRadius: '0.5rem',
                    padding: '1rem'
                  }}>
                    {userMessages.map((message, index) => (
                      <div key={message.id} style={{
                        marginBottom: index < userMessages.length - 1 ? '1rem' : '0',
                        paddingBottom: index < userMessages.length - 1 ? '1rem' : '0',
                        borderBottom: index < userMessages.length - 1 ? '1px solid #f3f4f6' : 'none'
                      }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                            <span style={{
                              padding: '0.25rem 0.5rem',
                              borderRadius: '0.25rem',
                              fontSize: '0.75rem',
                              fontWeight: '500',
                              backgroundColor: message.tipo === 'alerta' ? '#fef3c7' : '#eff6ff',
                              color: message.tipo === 'alerta' ? '#92400e' : '#1e40af'
                            }}>
                              {message.tipo === 'alerta' ? '🚨 Alerta' : '💬 Mensaje'}
                            </span>
                            <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                              de {message.sender?.name}
                            </span>
                          </div>
                          <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                            {new Date(message.createdAt).toLocaleString()}
                          </span>
                        </div>
                        <p style={{ margin: 0, color: '#374151' }}>
                          {message.contenido}
                        </p>
                        {!message.leido && (
                          <span style={{
                            fontSize: '0.75rem',
                            color: '#dc2626',
                            fontWeight: '500',
                            marginTop: '0.5rem',
                            display: 'block'
                          }}>
                            • No leído
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal para gestión de clientes asignados */}
      {showClientsModal && selectedOperator && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '0.5rem',
            padding: '1.5rem',
            width: '90%',
            maxWidth: '800px',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <div style={{ marginBottom: '1rem' }}>
              <h3 style={{ margin: 0, marginBottom: '0.5rem' }}>
                Gestionar Clientes - {selectedOperator.name}
              </h3>
              <p style={{ margin: 0, color: '#6b7280', fontSize: '0.875rem' }}>
                Asignar clientes específicos para restringir el acceso del operador solo a esos clientes
              </p>
            </div>

            {clientsLoading ? (
              <div style={{ textAlign: 'center', padding: '2rem' }}>
                <div>Cargando clientes...</div>
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
                {/* Lista de clientes disponibles para asignar */}
                <div>
                  <h4 style={{ margin: '0 0 1rem 0', color: '#374151' }}>
                    Clientes Disponibles ({availableClients.length})
                  </h4>
                  {availableClients.length === 0 ? (
                    <p style={{ color: '#6b7280', fontStyle: 'italic' }}>
                      Todos los clientes están asignados a este operador
                    </p>
                  ) : (
                    <div style={{ maxHeight: '300px', overflow: 'auto' }}>
                      {availableClients.map((client) => (
                        <div key={client.id} style={{
                          border: '1px solid #e5e7eb',
                          borderRadius: '0.375rem',
                          padding: '0.75rem',
                          marginBottom: '0.5rem',
                          backgroundColor: '#f9fafb'
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div>
                              <div style={{ fontWeight: '500' }}>{client.name || client.empresa}</div>
                              <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                                {client.email}
                              </div>
                            </div>
                            <button
                              onClick={() => handleAssignClient(client.id)}
                              style={{
                                padding: '0.25rem 0.5rem',
                                backgroundColor: '#10b981',
                                color: 'white',
                                border: 'none',
                                borderRadius: '0.25rem',
                                fontSize: '0.75rem',
                                cursor: 'pointer'
                              }}
                            >
                              Asignar
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Lista de clientes asignados */}
                <div>
                  <h4 style={{ margin: '0 0 1rem 0', color: '#374151' }}>
                    Clientes Asignados ({assignedClients.length})
                  </h4>
                  {assignedClients.length === 0 ? (
                    <div style={{ padding: '1rem', backgroundColor: '#fef3c7', border: '1px solid #f59e0b', borderRadius: '0.375rem' }}>
                      <p style={{ margin: 0, color: '#92400e', fontSize: '0.875rem', fontWeight: '500' }}>
                        ⚠️ Sin restricciones
                      </p>
                      <p style={{ margin: '0.25rem 0 0 0', color: '#92400e', fontSize: '0.75rem' }}>
                        Este operador puede acceder a todas las auditorías del sistema
                      </p>
                    </div>
                  ) : (
                    <div style={{ maxHeight: '400px', overflow: 'auto' }}>
                      {assignedClients.map((assigned) => (
                        <div key={assigned.id} style={{
                          border: '1px solid #d1fae5',
                          borderRadius: '0.375rem',
                          padding: '0.75rem',
                          marginBottom: '0.5rem',
                          backgroundColor: '#ecfdf5'
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontWeight: '500' }}>
                                {assigned.client.name || assigned.client.empresa}
                              </div>
                              <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '0.25rem' }}>
                                {assigned.client.email}
                              </div>
                              <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                                Asignado por: {assigned.assignedByUser?.name || 'N/A'}
                              </div>
                              <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                                Fecha: {new Date(assigned.createdAt).toLocaleDateString()}
                              </div>
                            </div>
                            <button
                              onClick={() => handleUnassignClient(assigned.clientId)}
                              style={{
                                padding: '0.25rem 0.5rem',
                                backgroundColor: '#ef4444',
                                color: 'white',
                                border: 'none',
                                borderRadius: '0.25rem',
                                fontSize: '0.75rem',
                                cursor: 'pointer',
                                marginLeft: '0.5rem'
                              }}
                            >
                              Remover
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Información importante */}
            <div style={{
              marginTop: '1.5rem',
              padding: '1rem',
              backgroundColor: '#eff6ff',
              border: '1px solid #3b82f6',
              borderRadius: '0.375rem'
            }}>
              <div style={{ fontSize: '0.875rem', color: '#1e40af' }}>
                <strong>💡 Información:</strong>
                <ul style={{ margin: '0.5rem 0', paddingLeft: '1.25rem' }}>
                  <li>Si no hay clientes asignados, el operador puede acceder a todas las auditorías</li>
                  <li>Si hay clientes asignados, el operador solo verá auditorías de esos clientes</li>
                  <li>Si no hay auditorías en los clientes asignados, se aplica fallback automático</li>
                </ul>
              </div>
            </div>

            {/* Botones de acción */}
            <div style={{ marginTop: '1.5rem', display: 'flex', justifyContent: 'flex-end' }}>
              <button
                onClick={closeClientsModal}
                style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: '#6b7280',
                  color: 'white',
                  border: 'none',
                  borderRadius: '0.375rem',
                  cursor: 'pointer'
                }}
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Dashboard de Impersonación */}
      {showUserDashboard && (
        <ImpersonationDashboard
          adminUser={user}
          targetUser={showUserDashboard}
          onExit={() => setShowUserDashboard(null)}
        />
      )}
    </div>
  );
};

export default UserManagement;